// Lone Rider — Moto Lights tab switcher
//
// The MTL9 / MTL4 tab component shared by the hotspots section and the specs
// section. One implementation, so the two cannot drift apart.
//
// It expects, inside a root carrying [data-ml-tabs]:
//
//   [data-ml-tab="1|2"]    the tab buttons, each with data-ml-tab-set="mtl4|mtl9|bundle"
//   [data-ml-panel="1|2"]  the panels they switch between
//   [data-ml-tab-cta]      optional CTA whose label and configurator preset follow
//                          the active tab. Its wording comes from
//                          data-ml-cta-template on the root, with [set] replaced by
//                          the active tab's label.
//
// WHY THIS EXISTS RATHER THAN A THEME COMPONENT
// The theme has two tab implementations and neither is reusable: lr-tabs.liquid
// suffixes every class with its own section id, and the product details popup's
// .ptabs__ are scoped inside that snippet. Neither has keyboard support either, so
// this follows the ARIA tabs pattern properly — roving tabindex and arrow keys.
(() => {
  'use strict';

  function bind(root) {
    if (root.__mlTabsBound) return;
    root.__mlTabsBound = true;

    const tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ml-tab]'));
    const panels = Array.prototype.slice.call(root.querySelectorAll('[data-ml-panel]'));
    const cta = root.querySelector('[data-ml-tab-cta]');
    const ctaTemplate = root.getAttribute('data-ml-cta-template') || 'Buy [set]';

    if (!tabs.length) return;

    function activate(index, focusTab) {
      tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        // Roving tabindex: only the active tab is in the tab order, which is what
        // the ARIA tabs pattern expects.
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focusTab) tab.focus();
      });

      panels.forEach((panel, i) => {
        const on = i === index;
        panel.classList.toggle('active', on);
        panel.hidden = !on;
      });

      // The CTA follows the tab: both its wording and which kit the popup opens on.
      if (cta) {
        const active = tabs[index];
        const set = active.getAttribute('data-ml-tab-set');
        if (set) cta.setAttribute('data-open-configurator', set);
        cta.textContent = ctaTemplate.replace('[set]', active.textContent.trim());
      }
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(i, false));

      tab.addEventListener('keydown', (e) => {
        const last = tabs.length - 1;
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = last;
        if (next === null) return;
        e.preventDefault();
        activate(next, true);
      });
    });

    // Theme editor: selecting a block should bring its tab forward, or the merchant
    // clicks something in the sidebar and nothing appears to happen. Which tab a
    // block belongs to is read from a map the section renders, because the blocks
    // themselves may carry nothing but {{ block.shopify_attributes }}.
    let blockTabs = {};
    const mapEl = root.querySelector('[data-ml-tab-block-map]');
    if (mapEl) {
      try { blockTabs = JSON.parse(mapEl.textContent || '{}'); } catch (e) { blockTabs = {}; }
    }
    document.addEventListener('shopify:block:select', (e) => {
      const tab = blockTabs[e.detail.blockId];
      if (!tab) return;
      activate(parseInt(tab, 10) - 1, false);
    });

    activate(0, false);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-ml-tabs]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.LRMotoLightsTabs = { init: init };
})();
